import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Medal, Loader2, Plus, Trash2, ExternalLink, CalendarClock, CheckSquare, Square,
  Star, ChevronLeft, Trophy, ChevronDown, ChevronUp, Upload, FileText, Download,
  User as UserIcon, Rocket, ShieldQuestion, AlertTriangle, Paperclip,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ESTADOS_CANDIDATURA = ["Propuesta", "En preparación", "Presentada", "Finalista", "Ganadora", "Descartada"];
const ESTADO_COLORS = {
  "Propuesta": "bg-slate-100 text-slate-700 border-slate-200",
  "En preparación": "bg-blue-100 text-blue-700 border-blue-200",
  "Presentada": "bg-amber-100 text-amber-700 border-amber-200",
  "Finalista": "bg-purple-100 text-purple-700 border-purple-200",
  "Ganadora": "bg-emerald-100 text-emerald-700 border-emerald-200",
  "Descartada": "bg-red-50 text-red-500 border-red-200",
};
const CONFIANZA_COLORS = {
  "MUY ALTA": "bg-emerald-100 text-emerald-700 border-emerald-200",
  "ALTA": "bg-blue-100 text-blue-700 border-blue-200",
  "MEDIA": "bg-amber-100 text-amber-700 border-amber-200",
  "CONDICIONADA": "bg-red-100 text-red-700 border-red-200",
};

function formatBytes(n) {
  if (!n) return "";
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function DeadlineBadge({ days, closed }) {
  if (closed) return <Badge className="bg-slate-200 text-slate-600 border-slate-300">Cerrado</Badge>;
  if (days == null) return null;
  const urgent = days <= 7;
  const warn = days <= 14;
  return (
    <Badge className={urgent ? "bg-red-100 text-red-700 border-red-200" : warn ? "bg-amber-100 text-amber-700 border-amber-200" : "bg-emerald-100 text-emerald-700 border-emerald-200"}>
      <CalendarClock className="w-3 h-3 mr-1" />{days} día(s) restantes
    </Badge>
  );
}

export default function AwardsPage() {
  const { token } = useAuth();
  const authH = { Authorization: `Bearer ${token}` };
  const [awards, setAwards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/awards`, { headers: authH });
      if (r.ok) setAwards(await r.json());
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (selected) {
    return <AwardDetail awardId={selected} authH={authH} onBack={() => { setSelected(null); load(); }} />;
  }

  return (
    <div className="space-y-6" data-testid="awards-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><Medal className="w-6 h-6 text-amber-600" />Premios de Innovación</h1>
        <p className="text-sm text-muted-foreground mt-1">Candidaturas a reconocimientos externos de visibilidad y reputación. Propón candidaturas y vota (1-5) para decidir qué presentar a cada premio.</p>
      </div>

      {loading ? (
        <div className="flex justify-center p-12"><Loader2 className="w-6 h-6 animate-spin text-amber-600" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="awards-grid">
          {awards.map((a, i) => {
            const done = (a.checklist || []).filter(c => c.done).length;
            const total = (a.checklist || []).length;
            return (
              <Card key={a.id} className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setSelected(a.id)} data-testid={`award-card-${i}`}>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <Trophy className="w-8 h-8 text-amber-500 flex-shrink-0" />
                    <DeadlineBadge days={a.days_until_deadline} closed={a.is_closed} />
                  </div>
                  <div>
                    <div className="font-bold text-sm leading-tight">{a.nombre}</div>
                    <div className="text-xs text-muted-foreground">{a.edicion} · {a.entidad_organizadora}</div>
                  </div>
                  <div className="text-[11px] text-muted-foreground">Cierre candidatura: <strong>{a.fecha_limite}</strong></div>
                  {total > 0 && (
                    <div>
                      <div className="h-1.5 bg-muted rounded-full overflow-hidden"><div className="h-full bg-amber-500" style={{ width: `${(done / total) * 100}%` }} /></div>
                      <div className="text-[10px] text-muted-foreground mt-1">{done}/{total} preparación completada</div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AwardDetail({ awardId, authH, onBack }) {
  const [award, setAward] = useState(null);
  const [candidatures, setCandidatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [aR, cR] = await Promise.all([
        fetch(`${API}/awards/${awardId}`, { headers: authH }),
        fetch(`${API}/awards/${awardId}/candidatures`, { headers: authH }),
      ]);
      if (aR.ok) setAward(await aR.json());
      if (cR.ok) setCandidatures(await cR.json());
    } finally { setLoading(false); }
  }, [awardId, authH]);

  useEffect(() => { load(); }, [load]);

  const toggleChecklist = async (item) => {
    const r = await fetch(`${API}/awards/${awardId}/checklist/${item.id}`, {
      method: "PATCH", headers: { ...authH, "Content-Type": "application/json" }, body: JSON.stringify({ done: !item.done }),
    });
    if (r.ok) load();
  };

  const vote = async (candId, score) => {
    const r = await fetch(`${API}/awards/candidatures/${candId}/vote`, {
      method: "POST", headers: { ...authH, "Content-Type": "application/json" }, body: JSON.stringify({ score }),
    });
    if (r.ok) { toast.success(`Voto registrado: ${score}/5`); load(); } else toast.error("Error al votar");
  };

  const changeEstado = async (candId, estado) => {
    const r = await fetch(`${API}/awards/candidatures/${candId}`, {
      method: "PUT", headers: { ...authH, "Content-Type": "application/json" }, body: JSON.stringify({ estado }),
    });
    if (r.ok) { toast.success("Estado actualizado"); load(); }
  };

  const deleteCandidature = async (candId) => {
    if (!window.confirm("¿Eliminar esta candidatura?")) return;
    const r = await fetch(`${API}/awards/candidatures/${candId}`, { method: "DELETE", headers: authH });
    if (r.ok) { toast.success("Candidatura eliminada"); load(); }
  };

  if (loading || !award) return <div className="flex justify-center p-12"><Loader2 className="w-6 h-6 animate-spin text-amber-600" /></div>;

  return (
    <div className="space-y-6" data-testid="award-detail">
      <Button variant="ghost" size="sm" onClick={onBack} data-testid="award-detail-back"><ChevronLeft className="w-4 h-4 mr-1" />Volver</Button>

      <Card>
        <CardContent className="p-5 space-y-3">
          <div className="flex items-start justify-between flex-wrap gap-2">
            <div>
              <h2 className="text-xl font-bold">{award.nombre}</h2>
              <div className="text-sm text-muted-foreground">{award.edicion} · {award.entidad_organizadora}</div>
            </div>
            <DeadlineBadge days={award.days_until_deadline} closed={award.is_closed} />
          </div>
          <p className="text-sm">{award.descripcion}</p>
          <div className="flex gap-4 text-xs text-muted-foreground flex-wrap">
            {award.periodo_presentacion && <span>Periodo de presentación: <strong>{award.periodo_presentacion}</strong></span>}
            <span>Cierre: <strong>{award.fecha_limite}</strong></span>
            {award.fecha_ceremonia && <span>Gala: {award.fecha_ceremonia}</span>}
            {award.fecha_resultados && <span>Resultados: {award.fecha_resultados}</span>}
            <span>Canal: {award.canal_presentacion}</span>
          </div>
          {award.url_referencia && (
            <a href={award.url_referencia} target="_blank" rel="noreferrer" className="text-xs text-blue-600 flex items-center gap-1 hover:underline" data-testid="award-url-link">
              <ExternalLink className="w-3 h-3" />Ver convocatoria oficial
            </a>
          )}
          <div className="flex flex-wrap gap-1.5 pt-1">
            {(award.categorias || []).map(c => <Badge key={c} variant="outline" className="text-[10px]">{c}</Badge>)}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-5">
          <h3 className="font-bold text-sm mb-3">Checklist de preparación</h3>
          <div className="space-y-1.5" data-testid="award-checklist">
            {(award.checklist || []).map((item) => (
              <div key={item.id} className="flex items-start gap-2 text-sm cursor-pointer" onClick={() => toggleChecklist(item)} data-testid={`checklist-item-${item.id}`}>
                {item.done ? <CheckSquare className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" /> : <Square className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />}
                <span className={item.done ? "line-through text-muted-foreground" : ""}>{item.texto}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <h3 className="font-bold">Candidaturas propuestas ({candidatures.length})</h3>
        <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="award-create-candidature-btn"><Plus className="w-4 h-4 mr-1" />Proponer candidatura</Button>
      </div>

      <div className="space-y-3" data-testid="candidatures-list">
        {candidatures.length === 0 && <Card><CardContent className="p-8 text-center text-muted-foreground text-sm">Sin candidaturas propuestas todavía</CardContent></Card>}
        {candidatures.map((c, i) => (
          <CandidatureCard key={c.id} c={c} i={i} authH={authH} vote={vote} changeEstado={changeEstado} deleteCandidature={deleteCandidature} onDocsChanged={load} />
        ))}
      </div>

      <CandidatureCreateDialog open={createOpen} onClose={() => setCreateOpen(false)} authH={authH} awardId={awardId} categorias={award.categorias} onCreated={() => { setCreateOpen(false); load(); }} />
    </div>
  );
}

function CandidatureCard({ c, i, authH, vote, changeEstado, deleteCandidature, onDocsChanged }) {
  const [expanded, setExpanded] = useState(false);
  const hasDetail = c.responsable || c.fecha_lanzamiento || c.nivel_confianza || c.indicadores_exito?.length || c.valores_diferenciales?.length || c.tecnologias?.length || c.ods?.length || c.riesgos_notas || c.documentos_requeridos?.length;

  return (
    <Card data-testid={`candidature-card-${i}`}>
      <CardContent className="p-4 space-y-2">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div>
            <div className="font-bold text-sm">{c.titulo}</div>
            {c.categoria && <div className="text-xs text-muted-foreground">{c.categoria}</div>}
          </div>
          <div className="flex items-center gap-2">
            {c.nivel_confianza && (
              <Badge className={CONFIANZA_COLORS[c.nivel_confianza] || ""} data-testid={`candidature-confianza-${c.id}`}>{c.nivel_confianza}</Badge>
            )}
            <Badge className={ESTADO_COLORS[c.estado] || ""}>{c.estado}</Badge>
            <Button size="sm" variant="ghost" className="h-7 text-red-500" onClick={() => deleteCandidature(c.id)} data-testid={`candidature-delete-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
          </div>
        </div>
        {c.descripcion && <p className="text-sm text-muted-foreground">{c.descripcion}</p>}
        {c.riesgos_notas && (
          <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900" data-testid={`candidature-riesgo-${c.id}`}>
            <AlertTriangle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800 dark:text-amber-300">{c.riesgos_notas}</p>
          </div>
        )}

        {hasDetail && (
          <button
            className="flex items-center gap-1 text-xs text-primary font-medium"
            onClick={() => setExpanded(!expanded)}
            data-testid={`candidature-expand-${c.id}`}
          >
            {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            {expanded ? "Ocultar detalle" : "Ver detalle y documentos"}
          </button>
        )}

        {expanded && (
          <div className="pt-2 space-y-3 border-t">
            <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
              {c.responsable && <span className="flex items-center gap-1"><UserIcon className="w-3.5 h-3.5" />{c.responsable}</span>}
              {c.fecha_lanzamiento && <span className="flex items-center gap-1"><Rocket className="w-3.5 h-3.5" />Lanzamiento: {c.fecha_lanzamiento}</span>}
            </div>
            {c.indicadores_exito?.length > 0 && (
              <ChipGroup label="Indicadores de éxito" items={c.indicadores_exito} testid={`candidature-kpis-${c.id}`} />
            )}
            {c.valores_diferenciales?.length > 0 && (
              <ChipGroup label="Valores diferenciales" items={c.valores_diferenciales} testid={`candidature-diff-${c.id}`} />
            )}
            {c.tecnologias?.length > 0 && (
              <ChipGroup label="Tecnologías" items={c.tecnologias} testid={`candidature-tech-${c.id}`} />
            )}
            {c.ods?.length > 0 && (
              <ChipGroup label="ODS" items={c.ods} testid={`candidature-ods-${c.id}`} />
            )}
            {c.documentos_requeridos?.length > 0 && (
              <div>
                <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Documentos a preparar</p>
                <ul className="text-xs text-muted-foreground list-disc list-inside space-y-0.5">
                  {c.documentos_requeridos.map((d, idx) => <li key={idx}>{d}</li>)}
                </ul>
              </div>
            )}
            <CandidatureDocuments candidature={c} authH={authH} onChanged={onDocsChanged} />
          </div>
        )}

        <div className="flex items-center justify-between flex-wrap gap-2 pt-1">
          <div className="flex items-center gap-1" data-testid={`candidature-vote-${c.id}`}>
            {[1, 2, 3, 4, 5].map(n => (
              <button key={n} onClick={() => vote(c.id, n)} data-testid={`candidature-vote-${c.id}-${n}`}>
                <Star className={`w-5 h-5 ${(c.my_score || 0) >= n ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
              </button>
            ))}
            <span className="text-xs text-muted-foreground ml-2">
              {c.avg_score != null ? `Media ${c.avg_score}/5 (${c.vote_count} voto${c.vote_count !== 1 ? "s" : ""})` : "Sin votos"}
            </span>
          </div>
          <Select value={c.estado} onValueChange={(v) => changeEstado(c.id, v)}>
            <SelectTrigger className="w-[160px] h-7 text-xs" data-testid={`candidature-estado-select-${c.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>{ESTADOS_CANDIDATURA.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </CardContent>
    </Card>
  );
}

function ChipGroup({ label, items, testid }) {
  return (
    <div data-testid={testid}>
      <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {items.map((it, idx) => <Badge key={idx} variant="outline" className="text-[10px] font-normal">{it}</Badge>)}
      </div>
    </div>
  );
}

function CandidatureDocuments({ candidature, authH, onChanged }) {
  const [uploading, setUploading] = useState(false);
  const [tipo, setTipo] = useState("");
  const fileRef = useRef(null);
  const docs = candidature.documentos || [];

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const params = tipo ? `?tipo=${encodeURIComponent(tipo)}` : "";
      const r = await fetch(`${API}/awards/candidatures/${candidature.id}/documents${params}`, {
        method: "POST", headers: authH, body: formData,
      });
      if (!r.ok) throw new Error((await r.json()).detail || `HTTP ${r.status}`);
      toast.success("Documento subido");
      setTipo("");
      onChanged();
    } catch (err) { toast.error(`Error al subir: ${err.message}`); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const handleDownload = async (doc) => {
    try {
      const r = await fetch(`${API}/awards/candidatures/${candidature.id}/documents/${doc.id}/download`, { headers: authH });
      if (!r.ok) throw new Error("No se pudo descargar");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch { toast.error("Error al descargar el documento"); }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm("¿Eliminar este documento?")) return;
    const r = await fetch(`${API}/awards/candidatures/${candidature.id}/documents/${docId}`, { method: "DELETE", headers: authH });
    if (r.ok) { toast.success("Documento eliminado"); onChanged(); }
  };

  return (
    <div data-testid={`candidature-documents-${candidature.id}`}>
      <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-1.5 flex items-center gap-1">
        <Paperclip className="w-3 h-3" />Documentos adjuntos ({docs.length})
      </p>
      {docs.length > 0 && (
        <div className="space-y-1.5 mb-2">
          {docs.map((d) => (
            <div key={d.id} className="flex items-center justify-between gap-2 p-2 rounded-md border text-xs" data-testid={`candidature-doc-${d.id}`}>
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                <span className="truncate">{d.original_filename}</span>
                {d.tipo && <Badge variant="secondary" className="text-[9px]">{d.tipo}</Badge>}
                <span className="text-muted-foreground flex-shrink-0">{formatBytes(d.size)}</span>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => handleDownload(d)} data-testid={`candidature-doc-download-${d.id}`}><Download className="w-3.5 h-3.5" /></Button>
                <Button size="icon" variant="ghost" className="h-6 w-6 text-red-500" onClick={() => handleDelete(d.id)} data-testid={`candidature-doc-delete-${d.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <Input
          placeholder="Tipo de documento (opcional)"
          value={tipo}
          onChange={(e) => setTipo(e.target.value)}
          className="h-8 text-xs w-56"
          data-testid={`candidature-doc-tipo-${candidature.id}`}
        />
        <Button size="sm" variant="outline" className="h-8 text-xs" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid={`candidature-doc-upload-btn-${candidature.id}`}>
          {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}Subir documento
        </Button>
        <input ref={fileRef} type="file" className="hidden" onChange={handleUpload} data-testid={`candidature-doc-file-input-${candidature.id}`} />
      </div>
    </div>
  );
}

function CandidatureCreateDialog({ open, onClose, authH, awardId, categorias, onCreated }) {
  const [form, setForm] = useState({ titulo: "", descripcion: "", categoria: "", linked_type: "manual", linked_id: "" });
  const [saving, setSaving] = useState(false);
  const [initiatives, setInitiatives] = useState([]);

  useEffect(() => {
    if (open) {
      setForm({ titulo: "", descripcion: "", categoria: "", linked_type: "manual", linked_id: "" });
      fetch(`${API}/initiatives`, { headers: authH }).then(r => r.ok ? r.json() : []).then(setInitiatives).catch(() => {});
    }
  }, [open, authH]);

  const pickInitiative = (id) => {
    const init = initiatives.find(i => i.id === id);
    const fallbackDesc = init?.description || init?.objective || init?.problem || "";
    setForm({ ...form, linked_id: id, titulo: init?.name || form.titulo, descripcion: fallbackDesc || form.descripcion });
  };

  const submit = async () => {
    if (!form.titulo.trim()) { toast.error("El título es obligatorio"); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API}/awards/${awardId}/candidatures`, {
        method: "POST", headers: { ...authH, "Content-Type": "application/json" }, body: JSON.stringify(form),
      });
      if (!r.ok) throw new Error((await r.json()).detail || `HTTP ${r.status}`);
      toast.success("Candidatura propuesta");
      onCreated();
    } catch (e) { toast.error(`Error: ${e.message}`); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="candidature-create-dialog">
        <DialogHeader><DialogTitle>Proponer candidatura</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Origen</Label>
            <Select value={form.linked_type} onValueChange={v => setForm({ ...form, linked_type: v, linked_id: "" })}>
              <SelectTrigger data-testid="candidature-create-linked-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="manual">Manual (escribir a mano)</SelectItem>
                <SelectItem value="initiative">Vincular proyecto del Portfolio</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.linked_type === "initiative" && (
            <div><Label className="text-xs">Proyecto del Portfolio</Label>
              <Select value={form.linked_id || ""} onValueChange={pickInitiative}>
                <SelectTrigger data-testid="candidature-create-linked-id"><SelectValue placeholder="Selecciona un proyecto" /></SelectTrigger>
                <SelectContent>{initiatives.map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div><Label className="text-xs">Título del proyecto/caso *</Label>
            <Input value={form.titulo} onChange={e => setForm({ ...form, titulo: e.target.value })} data-testid="candidature-create-titulo" /></div>
          <div><Label className="text-xs">Descripción / por qué encaja</Label>
            <Textarea rows={3} value={form.descripcion} onChange={e => setForm({ ...form, descripcion: e.target.value })} data-testid="candidature-create-descripcion" /></div>
          <div><Label className="text-xs">Categoría</Label>
            <Select value={form.categoria || ""} onValueChange={v => setForm({ ...form, categoria: v })}>
              <SelectTrigger data-testid="candidature-create-categoria"><SelectValue placeholder="Selecciona categoría" /></SelectTrigger>
              <SelectContent>{(categorias || []).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} data-testid="candidature-create-save">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}Proponer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
